import lodash from 'lodash';
import React, { useCallback, useContext, useMemo, useState } from 'react';

import { RicosContext } from './../../ricos-context';
import { GalleryOptions_Layout_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';

import styles from '../statics/styles/viewer.rtlignore-scss';
import { useIsPrerender } from './use-prerender';
import { useResizeObserver } from './use-resize-observer';

const THUMBNAIL_GALLERY_HEIGHT = 620;
const RESIZE_OBSERVER_DELAY = 100;
const defaultContainers = {
  desktop: {
    width: 980,
    height: 500,
  },
  mobile: {
    width: 320,
    height: 500,
  },
};

// This was originally implemented in Editor elements
// Since documentation is missing for pro-gallery, assuming this is the best way to get height in SSR
// https://github.com/wix-private/editor-elements/blob/d6e943b6ca5fac0daecae0b5e455b10055480a84/packages/editor-elements-library/src/components/ProGallery/viewer/ProGallery.tsx#L55
export const useHeight = (props: { options: any; layout: string }) => {
  const [isMeasuredContainer, setIsMeasuredContainer] = React.useState<any>(false);
  const isPrerender = useIsPrerender();
  const { options, layout } = props;
  const { responsive } = options;
  const { mobile, desktop } = defaultContainers;
  const { isMobile, jsdomMode } = useContext(RicosContext);

  const containerWidthResponsive = isMobile ? mobile.width : desktop.width;
  const containerHeightResponsive = isMobile ? mobile.height : desktop.height;

  const defaultContainer = {
    isDefaultContainer: true,
    width: containerWidthResponsive,
    height: containerHeightResponsive,
  };
  const [container, setContainer] = useState<any>(defaultContainer);
  const [height, setHeight] = useState<number>(container.height);
  const [containerRef, setContainerRef] = useState<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (isMeasuredContainer) {
      setIsMeasuredContainer(false);
      setContainer(defaultContainer);
    }
  }, [layout]);

  const resizeGallery = useCallback(() => {
    setContainer((prevState: $TSFixMe) => {
      // This is a workaround to TEMPORARILY fix the issue of the gallery shrinking in Safari
      // https://jira.wixpress.com/browse/RICH-1751
      // chat with progallery https://wix.slack.com/archives/CBTA3FEUW/p1727934603589929
      if (
        containerRef?.clientHeight &&
        prevState.height &&
        Math.abs(prevState.height - containerRef?.clientHeight) <= 1 &&
        Math.abs(prevState.width - containerRef?.clientWidth) <= 1
      ) {
        return prevState;
      }

      return {
        width: containerRef?.clientWidth,
        height: containerRef?.clientHeight,
      };
    });
    setIsMeasuredContainer(true);
  }, [containerRef, setIsMeasuredContainer]);

  const resizeObserverCallback = useMemo(
    () => lodash.debounce(resizeGallery, RESIZE_OBSERVER_DELAY),
    [resizeGallery]
  );
  useResizeObserver({
    element: containerRef,
    callback: resizeObserverCallback,
  });

  // Thumbnail galleries should have a fixed height
  const heightStyle =
    layout === GalleryOptions_Layout_Type.THUMBNAIL && !isMobile
      ? { height: THUMBNAIL_GALLERY_HEIGHT }
      : responsive
      ? {}
      : { height };
  const setHeightImp = (newHeight: number): void => {
    setHeight(newHeight);
  };

  const containerClassName = isPrerender ? styles.prerender : '';

  return {
    isMeasuredContainer: jsdomMode ? !isPrerender : isMeasuredContainer,
    setHeight: setHeightImp,
    container,
    setContainerRef,
    containerStyle: heightStyle,
    containerClassName,
  };
};
